import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SEO } from "../components/SEO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser, faBuilding, faEnvelope, faLock, faCircleExclamation,
  faUserPlus, faSpinner, faCheckCircle
} from "@fortawesome/free-solid-svg-icons";

export const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "", password: "", confirmPassword: "", agencyName: ""
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        navigate("/check-email", { state: { email: registeredEmail, requiresPayment: true } });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [success, navigate, registeredEmail]);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (!acceptedTerms) { setError("You must accept the Terms and Conditions and Privacy Policy"); setLoading(false); return; }
    if (formData.password !== formData.confirmPassword) { setError("Passwords do not match"); setLoading(false); return; }
    try {
      const backendUrl = process.env.REACT_APP_API_URL || "";
      const response = await fetch(backendUrl + "/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName: formData.firstName, lastName: formData.lastName, email: formData.email, password: formData.password, agencyName: formData.agencyName })
      });
      const data = await response.json();
      if (response.ok) { setRegisteredEmail(formData.email); setSuccess(true); }
      else setError(data.message || "Registration failed");
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(160deg, #1e1b4b 0%, #3730a3 100%)" }}>
        <div className="bg-white rounded-2xl shadow-glass p-10 text-center max-w-md w-full mx-4">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <FontAwesomeIcon icon={faCheckCircle} className="text-indigo-600 text-3xl" />
          </div>
          <p className="text-secondary-500 mb-2">We have sent a verification email to</p>
          <p className="font-semibold text-indigo-600 mb-5">{registeredEmail}</p>
          <div className="flex items-center justify-center gap-2 text-sm text-secondary-400">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Redirecting to verification instructions...
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Register | GHLWA - WhatsApp Integration for GoHighLevel"
        description="Create your GHLWA account and start integrating WhatsApp Business with your GoHighLevel locations."
        canonical="https://app.ghlwa.com/register"
      />
      <div className="min-h-screen flex">
        <div className="auth-left-panel">
          <div className="absolute top-[-80px] right-[-80px] w-72 h-72 rounded-full opacity-10" style={{ background: "#6366f1" }} />
          <div className="absolute bottom-[-60px] left-[-60px] w-56 h-56 rounded-full opacity-10" style={{ background: "#f59e0b" }} />
          <div className="relative z-10 flex flex-col items-center text-center">
            <img src="/ghlwa.png" alt="GHLWA Logo" className="h-24 w-auto mb-8 drop-shadow-xl" />
            <h1 className="text-4xl font-extrabold text-white mb-3 tracking-tight">GHLWA</h1>
            <p className="text-indigo-200 text-lg mb-10 max-w-xs leading-relaxed">Set up WhatsApp messaging for your agency in minutes</p>
            <div className="bg-white/10 rounded-2xl p-6 w-full max-w-xs text-left">
              <p className="text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-3">What you get</p>
              {["Unlimited location connections","Per-user or shared WhatsApp setup","Automatic GHL contact sync","Smart message throttling"].map((item, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-indigo-400 text-sm flex-shrink-0" />
                  <span className="text-indigo-100 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="auth-right-panel overflow-y-auto">
          <div className="w-full max-w-md">
            <div className="flex justify-center mb-8 lg:hidden">
              <img src="/ghlwa.png" alt="GHLWA" className="h-16 w-auto" />
            </div>
            <h2 className="text-3xl font-extrabold text-secondary-900 mb-2">Create your account</h2>
            <p className="text-secondary-500 mb-8">Begin routing WhatsApp messages to GHL</p>
            {error && (
              <div className="mb-5 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                <FontAwesomeIcon icon={faCircleExclamation} />{error}
              </div>
            )}
            <form className="space-y-4" onSubmit={handleRegister}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-secondary-700 mb-1.5">First Name</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-secondary-400 pointer-events-none"><FontAwesomeIcon icon={faUser} className="text-sm" /></span>
                    <input id="firstName" name="firstName" type="text" required value={formData.firstName} onChange={handleChange} className="input-field pl-10" placeholder="John" />
                  </div>
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-secondary-700 mb-1.5">Last Name</label>
                  <input id="lastName" name="lastName" type="text" required value={formData.lastName} onChange={handleChange} className="input-field" placeholder="Doe" />
                </div>
              </div>
              <div>
                <label htmlFor="agencyName" className="block text-sm font-medium text-secondary-700 mb-1.5">Agency Name</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-secondary-400 pointer-events-none"><FontAwesomeIcon icon={faBuilding} className="text-sm" /></span>
                  <input id="agencyName" name="agencyName" type="text" required value={formData.agencyName} onChange={handleChange} className="input-field pl-10" placeholder="Your Agency" />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-1.5">Email address</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-secondary-400 pointer-events-none"><FontAwesomeIcon icon={faEnvelope} className="text-sm" /></span>
                  <input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} className="input-field pl-10" placeholder="you@example.com" />
                </div>
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-1.5">Password</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-secondary-400 pointer-events-none"><FontAwesomeIcon icon={faLock} className="text-sm" /></span>
                  <input id="password" name="password" type="password" autoComplete="new-password" required value={formData.password} onChange={handleChange} className="input-field pl-10" placeholder="........" />
                </div>
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-secondary-700 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-secondary-400 pointer-events-none"><FontAwesomeIcon icon={faLock} className="text-sm" /></span>
                  <input id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" required value={formData.confirmPassword} onChange={handleChange} className="input-field pl-10" placeholder="........" />
                </div>
              </div>
              <div className="flex items-start gap-3 pt-1">
                <input id="acceptTerms" type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-0.5 w-4 h-4 accent-indigo-600 cursor-pointer" required />
                <label htmlFor="acceptTerms" className="text-sm text-secondary-600">
                  I accept the{" "}
                  <a href="https://legal.ghlwa.com/terms" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline font-medium">Terms and Conditions</a>
                  {" "}and{" "}
                  <a href="https://legal.ghlwa.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline font-medium">Privacy Policy</a>
                </label>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? (<><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Creating account...</>) : (<><FontAwesomeIcon icon={faUserPlus} /> Create account</>)}
              </button>
            </form>
            <p className="mt-6 text-center text-sm text-secondary-600">
              Already have an account?{" "}
              <a href="/login" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">Sign in</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
